/**
 * Self-managed GPT + header bidding provider.
 *
 * Orchestrates:
 *   1. Google Publisher Tag (GPT) — ad serving
 *   2. Prebid.js — header bidding (client-side + server-to-server)
 *   3. Amazon Publisher Services (APS) — header bidding
 *   4. LiveRamp ATS — identity resolution
 *
 * Bidding flow:
 *   Slots enter viewport → batched → Prebid + APS run in parallel →
 *   2 s failsafe timeout → targeting applied → googletag.pubads().refresh()
 */

const MAX_ADS_PER_PAGE = 20;
const BID_FAILSAFE_MS = 2000;
const PREBID_TIMEOUT_MS = 7000;
const DESKTOP_BREAKPOINT = 1024;

/** Responsive size mapping for banner ads. */
function buildBannerSizeMapping() {
  return globalThis.googletag
    .sizeMapping()
    .addSize([DESKTOP_BREAKPOINT, 90], [728, 90])
    .addSize([0, 0], [320, 50])
    .build();
}

/** Map variant names to GPT sizes. */
const VARIANT_SIZES = {
  banner: [
    [728, 90],
    [320, 50],
  ],
  "half-page": [[300, 600]],
  rectangle: [[300, 250]],
};

export class SelfManagedProvider {
  /** @type {Map<string, { el: HTMLElement, gptSlot: object }>} */
  #slots = new Map();
  #config;
  #bannerSizeMapping = null;

  // Batch queue: slots waiting for auction
  #pendingSlots = [];
  #batchTimer = null;

  /**
   * @param {object} config
   * @param {string} config.networkId — e.g. "/40776096/MeetMe"
   * @param {string} config.brandPrefix — e.g. "MeetMe"
   * @param {object} [config.prebid]
   * @param {object} [config.aps]
   * @param {object} [config.liveramp]
   * @param {object} [config.consent]
   */
  constructor(config) {
    this.#config = config;
  }

  initialize() {
    this.#initGpt();
    this.#initPrebid();
    this.#initAps();
    this.#initLiveRamp();
  }

  // ─── GPT ───────────────────────────────────────────────

  #initGpt() {
    globalThis.googletag = globalThis.googletag || {};
    globalThis.googletag.cmd = globalThis.googletag.cmd || [];

    globalThis.googletag.cmd.push(() => {
      const pubads = globalThis.googletag.pubads();

      // SRA: batch all slot requests into a single network call
      pubads.enableSingleRequest();

      // Lazy loading
      pubads.enableLazyLoad({
        fetchMarginPercent: 100,
        renderMarginPercent: 0,
        mobileScaling: 2,
      });

      // Don't fetch ads automatically — we trigger after bidding completes
      pubads.disableInitialLoad();

      // --- Privacy / Consent ---
      // const { consent } = this.#config;
      // if (consent?.gdpr) {
      //   pubads.setPrivacySetting('restrictDataProcessing', !consent.gdpr.personalised);
      // }
      // if (consent?.ccpa?.uspString) {
      //   pubads.setPrivacySetting('restrictDataProcessing', consent.ccpa.restrictDataProcessing);
      // }

      globalThis.googletag.enableServices();

      this.#bannerSizeMapping = buildBannerSizeMapping();
    });
  }

  // ─── Prebid ────────────────────────────────────────────

  #initPrebid() {
    const prebidCfg = this.#config.prebid;
    if (!prebidCfg?.enabled) return;

    globalThis.pbjs = globalThis.pbjs || {};
    globalThis.pbjs.que = globalThis.pbjs.que || [];

    globalThis.pbjs.que.push(() => {
      globalThis.pbjs.setConfig({
        bidderTimeout: PREBID_TIMEOUT_MS,
        // s2sConfig would go here when server-to-server is configured
        // s2sConfig: { ... }
      });

      // --- Consent management modules ---
      // window.pbjs.setConfig({
      //   consentManagement: {
      //     gdpr: { cmpApi: 'iab', timeout: 8000 },
      //     usp: { cmpApi: 'iab', timeout: 1000 },
      //   },
      // });
    });
  }

  // ─── Amazon APS ────────────────────────────────────────

  #initAps() {
    const apsCfg = this.#config.aps;
    if (!apsCfg?.enabled) return;

    // APS command queue — mirrors the GPT/Prebid pattern so calls are
    // buffered until the apstag library loads asynchronously.
    globalThis.apstag = globalThis.apstag || {};
    globalThis.apstag._Q = globalThis.apstag._Q || [];
    if (typeof globalThis.apstag.init !== "function") {
      for (const method of ["init", "fetchBids", "setDisplayBids", "rpa"]) {
        globalThis.apstag[method] ||= (...args) =>
          globalThis.apstag._Q.push([method, args]);
      }
    }

    globalThis.apstag.init({
      pubID: apsCfg.pubId,
      adServer: "googletag",
      simplerGPT: true,
      bidTimeout: BID_FAILSAFE_MS,
    });
  }

  // ─── LiveRamp ATS ──────────────────────────────────────

  #initLiveRamp() {
    const lrCfg = this.#config.liveramp;
    if (!lrCfg?.enabled) return;

    // LiveRamp is loaded via a <script> tag injected server-side.
    // Pass hashed email when available.
    // if (lrCfg.hashedEmail) {
    //   window.ats?.setAdditionalData({ type: 'emailHashes', id: [lrCfg.hashedEmail] });
    // }
  }

  // ─── Slot registration ─────────────────────────────────

  /**
   * Called by <ad-slot> when it enters the viewport.
   * @param {HTMLElement} slotEl
   */
  registerSlot(slotEl) {
    const slotId = slotEl.slotId;
    if (!slotId || this.#slots.size >= MAX_ADS_PER_PAGE) return;
    if (this.#slots.has(slotId)) return;

    this.#slots.set(slotId, { el: slotEl, gptSlot: null });
    this.#pendingSlots.push(slotEl);
    this.#scheduleBatch();
  }

  /**
   * Called by <ad-slot> on disconnect.
   * @param {HTMLElement} slotEl
   */
  unregisterSlot(slotEl) {
    const slotId = slotEl.slotId;
    const record = this.#slots.get(slotId);
    if (!record) return;

    if (record.gptSlot) {
      globalThis.googletag.cmd.push(() => {
        globalThis.googletag.destroySlots([record.gptSlot]);
      });
    }
    this.#slots.delete(slotId);
  }

  // ─── Batched auction ───────────────────────────────────

  /**
   * Debounce slot registrations so that multiple slots entering the viewport
   * in the same frame are auctioned together.
   */
  #scheduleBatch() {
    if (this.#batchTimer) return;
    this.#batchTimer = requestAnimationFrame(() => {
      this.#batchTimer = null;
      const batch = this.#pendingSlots.splice(0);
      if (batch.length > 0) this.#runAuction(batch);
    });
  }

  /**
   * Define GPT slots, run Prebid + APS in parallel, then refresh.
   * @param {HTMLElement[]} slotEls
   */
  #runAuction(slotEls) {
    globalThis.googletag.cmd.push(() => {
      const gptSlots = [];

      for (const el of slotEls) {
        const sizes = VARIANT_SIZES[el.variant] || [[300, 250]];
        const adUnitPath = `${this.#config.networkId}/${el.unitPath}`;

        const gptSlot = globalThis.googletag.defineSlot(
          adUnitPath,
          sizes,
          el.slotId,
        );
        if (!gptSlot) continue;

        // Apply responsive size mapping for banners
        if (el.variant === "banner" && this.#bannerSizeMapping) {
          gptSlot.defineSizeMapping(this.#bannerSizeMapping);
        }

        // Collapse behaviour
        if (!el.noCollapse) {
          gptSlot.setCollapseEmptyDiv(true, true);
        }

        gptSlot.addService(globalThis.googletag.pubads());
        globalThis.googletag.display(el.slotId);

        // Store the GPT slot reference for later cleanup
        const record = this.#slots.get(el.slotId);
        if (record) record.gptSlot = gptSlot;

        gptSlots.push(gptSlot);
      }

      if (gptSlots.length === 0) return;

      // Run header bidding, then refresh
      this.#requestBids(slotEls, gptSlots);
    });
  }

  /**
   * Run Prebid + APS in parallel with a failsafe timeout,
   * then apply targeting and refresh GPT.
   * @param {HTMLElement[]} slotEls
   * @param {object[]} gptSlots
   */
  #requestBids(slotEls, gptSlots) {
    const prebidEnabled = this.#config.prebid?.enabled;
    const apsEnabled = this.#config.aps?.enabled;

    // If no bidders, just refresh immediately
    if (!prebidEnabled && !apsEnabled) {
      globalThis.googletag.pubads().refresh(gptSlots);
      return;
    }

    let prebidDone = !prebidEnabled;
    let apsDone = !apsEnabled;
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;

      if (apsEnabled) {
        globalThis.apstag.setDisplayBids();
      }

      // When Prebid is enabled, apply targeting and refresh inside its queue
      // to guarantee setTargetingForGPTAsync() completes before refresh().
      if (prebidEnabled) {
        globalThis.pbjs?.que.push(() => {
          globalThis.pbjs.setTargetingForGPTAsync();
          globalThis.googletag.pubads().refresh(gptSlots);
        });
      } else {
        globalThis.googletag.pubads().refresh(gptSlots);
      }
    };

    const checkDone = () => {
      if (prebidDone && apsDone) finish();
    };

    // Failsafe: proceed regardless after timeout
    const failsafe = setTimeout(finish, BID_FAILSAFE_MS);
    const onAllDone = () => clearTimeout(failsafe);

    // --- Prebid ---
    if (prebidEnabled) {
      const adUnits = slotEls.map((el) => ({
        code: el.slotId,
        mediaTypes: {
          banner: {
            sizes: VARIANT_SIZES[el.variant] || [[300, 250]],
          },
        },
        bids: this.#config.prebid.bidders || [],
      }));

      globalThis.pbjs?.que.push(() => {
        globalThis.pbjs.addAdUnits(adUnits);
        globalThis.pbjs.requestBids({
          timeout: PREBID_TIMEOUT_MS,
          bidsBackHandler: () => {
            prebidDone = true;
            checkDone();
            if (apsDone) onAllDone();
          },
        });
      });
    }

    // --- Amazon APS ---
    if (apsEnabled) {
      const apsSlots = slotEls.map((el) => ({
        slotID: el.slotId,
        slotName: `${this.#config.networkId}/${el.unitPath}`,
        sizes: VARIANT_SIZES[el.variant] || [[300, 250]],
      }));

      globalThis.apstag.fetchBids({ slots: apsSlots }, () => {
        apsDone = true;
        checkDone();
        if (prebidDone) onAllDone();
      });
    }
  }
}
